// dashboard.tsx — an observational operations home (no publish/deploy actions here).
// Layout: a status/rates/your-changes row, then site traffic (hero), then the team's recent
// changes + translation coverage.
import React, { useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import type { LogEntry } from "../API/Models";
import { useLogsData } from "../hooks/useLogsData";
import useNetworkStatusData from "../hooks/useNetworkStatusData";
import { useTranslationCoverage } from "../hooks/useDashboardExtras";
import { useRealtime, useRealtimeEvent } from "../Realtime/RealtimeProvider";
import { activityToLogEntry, mergeActivityEntry, type ActivityPayload } from "../Realtime/activityStream";
import { IdentityStrip } from "../Components/dashboard/IdentityStrip";
import { ServiceStatus } from "../Components/dashboard/ServiceStatus";
import { ShopPulse } from "../Components/dashboard/ShopPulse";
import { ChangesFeed } from "../Components/dashboard/ChangesFeed";
import { AnalyticsSection } from "../Components/dashboard/AnalyticsSection";
import { ArticleCards } from "../Components/dashboard/ArticleCards";
import { TranslationCoverage } from "../Components/dashboard/TranslationCoverage";

const LOG_LIMIT = 40;
const FEED_CAP = 80;

const Dashboard: React.FC = () => {
	const { user } = useAuth0();
	const userName = user?.given_name ?? user?.nickname ?? user?.name ?? user?.email ?? "Admin";

	const { connected, online, users } = useRealtime();

	// Activity: polled then kept live by the "activity" SignalR channel. Feeds both change panels.
	const { logs: polledLogs, loading: logsLoading } = useLogsData({ limit: LOG_LIMIT, includeDebug: false });
	const [feed, setFeed] = useState<LogEntry[]>([]);
	useEffect(() => {
		setFeed(polledLogs);
	}, [polledLogs]);
	useRealtimeEvent("activity", (payload: ActivityPayload) => {
		setFeed((prev) => mergeActivityEntry(prev, activityToLogEntry(payload), FEED_CAP));
	});

	const { data: networkData } = useNetworkStatusData(12);
	const { data: coverage, loading: coverageLoading } = useTranslationCoverage();

	return (
		<div className="min-h-full overflow-x-hidden bg-slate-50 px-4 py-5 sm:px-6 sm:py-6 lg:px-8">
			<div className="w-full space-y-5">
				<IdentityStrip userName={userName} connected={connected} online={online} users={users} />

				{/* Hero — edge traffic for every public instance + the visitor map, the first thing an admin checks. */}
				<AnalyticsSection />

				{/* Three equal-height glances: service health, key rates, your own changes. */}
				<div className="grid min-w-0 grid-cols-1 items-stretch gap-5 lg:grid-cols-3">
					<ServiceStatus targets={networkData?.targets ?? []} connected={connected} />
					<ShopPulse />
					<ChangesFeed logs={feed} loading={logsLoading} currentUser={userName} scope="me" title="Mes dernières modifications" limit={6} />
				</div>

				{/* Four cards — the latest articles, each with its illustration + summary. */}
				<ArticleCards />

				{/* Row 3 — two long panels: the whole team's recent changes, and translation coverage. */}
				<div className="grid min-w-0 grid-cols-1 gap-5 lg:grid-cols-2">
					<ChangesFeed
						logs={feed}
						loading={logsLoading}
						currentUser={userName}
						scope="all"
						title="Toutes les modifications récentes"
						subtitle="Toute l'équipe"
						limit={14}
						connected={connected}
						bodyClassName="max-h-[380px] overflow-auto"
					/>
					<TranslationCoverage data={coverage} loading={coverageLoading} />
				</div>
			</div>
		</div>
	);
};

export { Dashboard };
export default Dashboard;
